import * as R from 'ramda';
import { Client, User } from '../types/api';
import { parseStartDate, parseEndDate } from '../utils/parseDate';
import { initialValuesSendData } from './initialValues';
import i18n from '@app/i18n/config';
import { rhAttributionPostQueryFields } from './rhAttributionPostQueryFields';
import { normalizeBroadcastCountries } from './broadcastCountries';
declare let __PROD_API__;

const KEYWORD_PLACEMENT_TYPE_ID = 267;
const HASHTAG_PLACEMENT_TYPE_ID = 268;
const MENTION_PLACEMENT_TYPE_ID = 269;

const getPlacement = (
    placementSelectionType: 'single' | 'all',
    placementId: string | false | null | undefined,
    placements,
) => {
    const _placement =
        placementSelectionType === 'single' &&
        placementId &&
        R.path(['list', placementId.split(',')[0]], placements ?? {});

    if (!_placement) return null;

    if (_placement.name.endsWith('- Hashtag')) {
        return {
            id: HASHTAG_PLACEMENT_TYPE_ID,
            name: 'Hashtag Text Exposure',
            isPlacementType: true,
        };
    }
    if (_placement.name.endsWith('- Mention')) {
        return {
            id: MENTION_PLACEMENT_TYPE_ID,
            name: 'Mention Text Exposure',
            isPlacementType: true,
        };
    }
    if (_placement.name.endsWith('- Keyword')) {
        return {
            id: KEYWORD_PLACEMENT_TYPE_ID,
            name: 'Keyword Text Exposure',
            isPlacementType: true,
        };
    }
    return _placement;
};

export default ({
    user,
    filters,
    client,
    values,
    rightsholderAttribution,
}: {
    user: User;
    filters: any;
    client: Client;
    values: any;
    rightsholderAttribution?: string;
}) => {
    const { token, email, gumgumUser, firstName, lastName, id: userId } = user;

    const {
        sponsors = {},
        rightsholders = {},
        placements = {},
    } = filters ?? {};
    const { currency, primaryColor, secondaryColor } = client;
    const rhWithSP = values?.rhWithSP ?? [];
    const selectedRightsholders = values?.rightsholders ?? {};
    const selectedSponsors = values?.sponsors ?? [];
    const broadcastCountries = normalizeBroadcastCountries(
        values?.broadcastCountryIds,
        filters?.countries?.list,
        values?.broadcastCountryNames,
    );

    const rightsholderIds = rhWithSP.reduce((acc, rhId) => {
        if (selectedRightsholders[rhId]) {
            acc.push(rhId);
        }

        return acc;
    }, []);
    const multipleRightsholders = rightsholderIds.length > 1;

    const startDate =
        values?.dateSelectionType == 'specific'
            ? values?.startDate
                ? parseStartDate(values.startDate)
                : parseStartDate(initialValuesSendData.startDate)
            : null;

    const endDate =
        values?.dateSelectionType == 'specific'
            ? values?.endDate
                ? parseEndDate(values.endDate)
                : parseEndDate(initialValuesSendData.endDate)
            : null;

    const env = __PROD_API__ ? 'production' : 'development';

    const rightsholder = rightsholderIds
        .map(rhId => R.path(['list', rhId], rightsholders))
        .filter(Boolean);

    const rightsholderNames = rightsholder.map(rh => rh.name);
    const sponsorName =
        selectedSponsors.length === 1
            ? sponsors?.list?.[selectedSponsors[0]]?.name
            : `${selectedSponsors.length} Sponsors`;

    const rightsholderLogoUrl = multipleRightsholders
        ? R.path(['headerImageUrl'], client)
        : R.path(['mainLogo', 'url'], rightsholder[0]);

    const placementId =
        values?.placementSelectionType === 'single' &&
        values?.placement &&
        rightsholder.length &&
        rightsholder[0] &&
        `${rightsholder[0].id}-${values.placement}`;

    const placement = getPlacement(
        values?.placementSelectionType,
        placementId,
        placements,
    );

    const filterData = {
        rightsholderIds,
        rightsholderNames,
        sponsorIds: selectedSponsors,
        sponsorName,
        placementSelection: values?.placementSelectionType,
        ...(placement?.isPlacementType
            ? {
                  placementTypeId: placement.id,
              }
            : {
                  placementId: placement && placement.id,
              }),
        placementName: placement && placement.name,
        filteredPlacements: values?.filteredPlacements,
        endDate,
        startDate,
        rightsholderLogoUrl,
    };
    const { goal, goalName, includesMethodologySlides } = values ?? {};

    const timezoneOffset = new Date().getTimezoneOffset();

    return {
        ...(goal
            ? {
                  goal,
                  goalName,
              }
            : filterData),
        currency: currency?.code,
        locale: i18n.language || 'en-US',
        primaryColor,
        secondaryColor,
        orderBy: values?.sortBy,
        postImgHeight: values?.postImgHeight,
        postImgWidth: values?.postImgWidth,
        postImgUrl: values?.postImgUrl,
        heroPostId: values?.heroPostId,
        clientId: client.id,
        reportTitle: values?.reportTitle,
        exportAs: values?.exportAs,
        userId,
        email,
        firstName,
        lastName,
        gumgumUser,
        token,
        env,
        includesMethodologySlides,
        timezoneOffset,
        ...broadcastCountries,
        ...rhAttributionPostQueryFields(client, rightsholderAttribution),
    };
};
